import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { toast } from 'sonner';
import { Plus, Trash2, Save, X, Search, Edit3, Boxes } from 'lucide-react';
import ImageUpload from '@/components/ImageUpload';
import MultiImageUpload from '@/components/MultiImageUpload';

const emptyProduct = () => ({
  slug: '', name: '', subtitle: 'Heritage Signature Collection',
  short_description: '', description: '',
  price: 0, sale_price: '', currency: 'NZD',
  product_type: 'apparel', category: '',
  collection_name: 'Heritage Signature Collection', colourway_swatch: '',
  image_url: '', images: [], video_url: '',
  highlights: [], features: [], materials: [], care_instructions: [], heritage_details: [],
  fit_notes: '', size_notes: '',
  variants: [], status: 'published', active: true, is_shop_product: true, order_index: 0,
});

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="h-eyebrow mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] opacity-70 mt-1" style={{ color: 'var(--heritage-plum)' }}>{hint}</div>}
    </label>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className={`w-full rounded-md border px-3 py-2 text-sm bg-white ${props.className || ''}`}
      style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)', ...(props.style || {}) }}
    />
  );
}

function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full rounded-md border px-3 py-2 text-sm bg-white ${props.className || ''}`}
      style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)', ...(props.style || {}) }}
    />
  );
}

// Comma-separated list editor as one-per-line textarea
function ListEditor({ label, value, onChange, hint }) {
  return (
    <Field label={label} hint={hint || 'One per line.'}>
      <Textarea
        rows={4}
        value={(value || []).join('\n')}
        onChange={(e) => onChange(e.target.value.split('\n').map((s) => s.trimEnd()).filter((s) => s !== ''))}
      />
    </Field>
  );
}

function VariantEditor({ product, onChange }) {
  const variants = product.variants || [];
  const setVariants = (v) => onChange({ ...product, variants: v });

  const addRow = () => {
    const suffix = (variants.length + 1).toString().padStart(2, '0');
    setVariants([
      ...variants,
      { sku: `${product.slug || 'sku'}-${suffix}`, size: product.product_type === 'apparel' ? 'M' : null, colour: '', colour_hex: '', stock: 0, active: true },
    ]);
  };

  const updateRow = (idx, patch) => {
    setVariants(variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };

  const removeRow = (idx) => setVariants(variants.filter((_, i) => i !== idx));

  const totalStock = variants.reduce((n, v) => n + Number(v.stock || 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="h-eyebrow">Variants</div>
          <div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>Total stock: <b>{totalStock}</b> units across {variants.length} variant{variants.length === 1 ? '' : 's'}.</div>
        </div>
        <button type="button" onClick={addRow} className="h-btn-outline text-xs" data-testid="variant-add">
          <Plus size={12} className="mr-1" /> Add variant
        </button>
      </div>

      {variants.length === 0 && (
        <div className="text-xs opacity-75 border border-dashed rounded-md p-4 text-center" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}>
          No variants yet. Add at least one so shoppers can buy this product.
        </div>
      )}

      <div className="space-y-2">
        {variants.map((v, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center rounded-md p-2" style={{ background: 'rgba(75,24,56,0.04)' }} data-testid={`variant-row-${i}`}>
            <Input value={v.sku} onChange={(e) => updateRow(i, { sku: e.target.value })} placeholder="SKU" className="col-span-3 text-xs" data-testid={`variant-sku-${i}`} />
            <Input value={v.size || ''} onChange={(e) => updateRow(i, { size: e.target.value || null })} placeholder="Size" className="col-span-1 text-xs" data-testid={`variant-size-${i}`} />
            <Input value={v.colour || ''} onChange={(e) => updateRow(i, { colour: e.target.value })} placeholder="Colour" className="col-span-3 text-xs" data-testid={`variant-colour-${i}`} />
            <div className="col-span-2 flex items-center gap-2">
              <Input type="color" value={v.colour_hex || '#9A4B7E'} onChange={(e) => updateRow(i, { colour_hex: e.target.value })} className="w-10 h-8 p-0.5" />
              <Input value={v.colour_hex || ''} onChange={(e) => updateRow(i, { colour_hex: e.target.value })} placeholder="#hex" className="text-xs" />
            </div>
            <Input type="number" min={0} value={v.stock ?? 0} onChange={(e) => updateRow(i, { stock: Number(e.target.value) })} placeholder="Stock" className="col-span-2 text-xs" data-testid={`variant-stock-${i}`} />
            <div className="col-span-1 flex justify-end">
              <button type="button" onClick={() => removeRow(i)} className="opacity-70 hover:opacity-100" style={{ color: '#9A2C2C' }} data-testid={`variant-remove-${i}`} aria-label="Remove variant">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function generateApparelMatrix(product) {
  const colours = [
    { name: 'Pinkle', hex: '#9A4B7E' },
    { name: 'F**ken Green', hex: '#8FA34B' },
  ];
  const sizes = ['S', 'M', 'L'];
  const slug = product.slug || 'product';
  const variants = [];
  colours.forEach((c) => {
    sizes.forEach((s) => {
      const key = c.name.toLowerCase().replace(/\*/g, '').replace(/\s+/g, '-');
      variants.push({
        sku: `ha-${slug}-${key}-${s}`.toLowerCase(),
        size: s, colour: c.name, colour_hex: c.hex, stock: 15, active: true,
      });
    });
  });
  return variants;
}

function ProductEditor({ product, onClose, onSaved }) {
  const [form, setForm] = useState(product);
  const [saving, setSaving] = useState(false);
  const isNew = !form.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.slug || !form.name) {
      toast.error('Slug and name are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        sale_price: form.sale_price === '' || form.sale_price == null ? null : Number(form.sale_price),
        order_index: Number(form.order_index) || 0,
        video_url: form.video_url || '',
        variants: (form.variants || []).map((v) => ({
          ...v,
          stock: Number(v.stock) || 0,
          size: v.size || null,
        })),
      };
      if (isNew) {
        await api.post('/admin/shop/products', payload);
        toast.success('Product created');
      } else {
        await api.put(`/admin/shop/products/${form.id}`, payload);
        toast.success('Saved');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-3" style={{ background: 'rgba(75,24,56,0.55)' }} onClick={onClose}>
      <div className="w-full max-w-4xl rounded-2xl overflow-hidden max-h-[92vh] flex flex-col" style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} onClick={(e) => e.stopPropagation()} data-testid="product-editor">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <div>
            <div className="h-eyebrow">{isNew ? 'New product' : 'Edit product'}</div>
            <div className="h-display text-2xl mt-0.5" style={{ color: 'var(--heritage-plum)' }}>{form.name || 'Untitled product'}</div>
          </div>
          <button onClick={onClose} className="p-1" aria-label="Close" style={{ color: 'var(--heritage-plum)' }} data-testid="product-editor-close"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Name"><Input value={form.name} onChange={(e) => { set('name', e.target.value); if (isNew && !form.slug) set('slug', slugify(e.target.value)); }} placeholder="Signature Pullover Hoodie" data-testid="prod-name" /></Field>
            <Field label="Slug" hint="URL: /store/{slug}"><Input value={form.slug} onChange={(e) => set('slug', slugify(e.target.value))} placeholder="signature-pullover-hoodie" data-testid="prod-slug" /></Field>
            <Field label="Subtitle"><Input value={form.subtitle} onChange={(e) => set('subtitle', e.target.value)} /></Field>
            <Field label="Category"><Input value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="Hoodie, Pants, Socks..." /></Field>
            <Field label="Price (NZD)"><Input type="number" value={form.price} onChange={(e) => set('price', e.target.value)} data-testid="prod-price" /></Field>
            <Field label="Sale price" hint="Optional. Overrides price on the storefront."><Input type="number" value={form.sale_price ?? ''} onChange={(e) => set('sale_price', e.target.value)} /></Field>
            <Field label="Product type">
              <select value={form.product_type} onChange={(e) => set('product_type', e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}>
                <option value="apparel">Apparel (size × colour)</option>
                <option value="socks">Socks (colour only)</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => set('status', e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
            <Field label="Hero image" hint="Upload from your device or paste a URL.">
              <ImageUpload
                value={form.image_url}
                onChange={(v) => set('image_url', v)}
                label=""
                testId="prod-image-upload"
              />
            </Field>
            <Field label="Order index"><Input type="number" value={form.order_index} onChange={(e) => set('order_index', e.target.value)} /></Field>
          </div>

          {/* Gallery images (up to 6) */}
          <Panel className="p-4">
            <MultiImageUpload
              value={form.images || []}
              onChange={(next) => set('images', next)}
              max={6}
              heroValue={form.image_url}
              onSetHero={(src) => set('image_url', src)}
              label="Gallery images"
              testId="prod-gallery-upload"
            />
          </Panel>

          <Field label="Showcase video (optional)" hint="Paste a direct MP4 URL, YouTube link, or Vimeo link. Shown below the gallery on the product page.">
            <Input value={form.video_url} onChange={(e) => set('video_url', e.target.value)} placeholder="https://www.youtube.com/watch?v=... or https://cdn.example.com/clip.mp4" data-testid="prod-video-url" />
          </Field>

          {/* image preview handled inside ImageUpload; remove old preview */}

          <Field label="Short description" hint="1 line under the product name.">
            <Input value={form.short_description} onChange={(e) => set('short_description', e.target.value)} />
          </Field>
          <Field label="Description">
            <Textarea rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <ListEditor label="Highlights" value={form.highlights} onChange={(v) => set('highlights', v)} />
            <ListEditor label="Features" value={form.features} onChange={(v) => set('features', v)} />
            <ListEditor label="Materials" value={form.materials} onChange={(v) => set('materials', v)} />
            <ListEditor label="Care instructions" value={form.care_instructions} onChange={(v) => set('care_instructions', v)} />
            <ListEditor label="Heritage details" value={form.heritage_details} onChange={(v) => set('heritage_details', v)} />
            <Field label="Fit notes"><Textarea rows={3} value={form.fit_notes} onChange={(e) => set('fit_notes', e.target.value)} /></Field>
          </div>

          <Panel className="p-4">
            <VariantEditor product={form} onChange={setForm} />
            {form.product_type === 'apparel' && form.variants.length === 0 && (
              <button type="button" onClick={() => setForm({ ...form, variants: generateApparelMatrix(form) })} className="h-btn-outline text-xs mt-3" data-testid="variant-generate-matrix">
                Generate S/M/L × Pinkle/F**ken Green matrix
              </button>
            )}
          </Panel>
        </div>

        <div className="px-6 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <button onClick={onClose} className="h-btn-outline text-xs" data-testid="product-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="h-btn text-xs" data-testid="product-save">
            <Save size={12} className="mr-1" /> {saving ? 'Saving...' : (isNew ? 'Create product' : 'Save changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminShopProducts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/shop/products');
      setItems(data.products || []);
    } catch { toast.error('Failed to load products'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return items;
    return items.filter((p) => (p.name || '').toLowerCase().includes(s) || (p.slug || '').toLowerCase().includes(s));
  }, [items, q]);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/shop/products/${p.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-shop-products">
      <div className="h-panel-thin p-6 flex items-center justify-between">
        <div>
          <div className="h-eyebrow">Store</div>
          <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Products & Inventory</div>
          <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>Edit prices, stock, variants and copy. Changes go live instantly.</p>
        </div>
        <button onClick={() => setEditing(emptyProduct())} className="h-btn text-xs" data-testid="product-new">
          <Plus size={12} className="mr-1" /> New product
        </button>
      </div>

      <Panel className="p-4">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-2.5 opacity-60" style={{ color: 'var(--heritage-plum)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or slug"
            className="pl-8 pr-3 py-1.5 rounded-full text-sm w-full" style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#FBF8F1', color: 'var(--heritage-plum)' }}
            data-testid="products-search" />
        </div>
      </Panel>

      {loading && <div className="h-eyebrow">Loading...</div>}

      {!loading && filtered.length === 0 && (
        <Panel className="p-8 text-center">
          <div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }}>No products yet</div>
          <div className="text-sm opacity-80 mt-1" style={{ color: 'var(--heritage-plum)' }}>Create the first Heritage product.</div>
        </Panel>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <Panel key={p.id} className="p-3" data-testid={`product-card-${p.slug}`}>
            <div className="flex gap-3">
              <img src={p.image_url || 'https://via.placeholder.com/120x150'} alt={p.name} className="w-24 h-28 object-cover rounded-md flex-shrink-0" style={{ background: '#FBF8F1' }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="h-serif text-base leading-tight truncate" style={{ color: 'var(--heritage-plum)' }}>{p.name}</div>
                    <div className="text-[10px] opacity-70 truncate" style={{ color: 'var(--heritage-plum)' }}>{p.slug}</div>
                  </div>
                  <span className="h-chip text-[9px]" style={{ background: p.status === 'published' ? 'rgba(91,110,44,0.18)' : 'rgba(75,24,56,0.08)', borderColor: 'transparent', color: 'var(--heritage-plum)' }}>{p.status || 'draft'}</span>
                </div>
                <div className="text-sm mt-1" style={{ color: 'var(--heritage-plum)' }}>${p.price} {p.currency}{p.sale_price && <span className="opacity-70"> (sale ${p.sale_price})</span>}</div>
                <div className="text-xs mt-1 flex items-center gap-1.5" style={{ color: 'var(--heritage-plum)' }}>
                  <Boxes size={12} /> {p.total_stock} in stock · {(p.variants || []).length} variant{(p.variants || []).length === 1 ? '' : 's'}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
              <button onClick={() => setEditing({ ...p, sale_price: p.sale_price ?? '' })} className="h-btn-outline text-xs" data-testid={`product-edit-${p.slug}`}><Edit3 size={12} className="mr-1" /> Edit</button>
              <button onClick={() => remove(p)} className="text-xs inline-flex items-center gap-1 opacity-80 hover:opacity-100" style={{ color: '#9A2C2C' }} data-testid={`product-delete-${p.slug}`}>
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </Panel>
        ))}
      </div>

      {editing && <ProductEditor product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
